import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useLocalCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { clarityEvent } from '../utils/clarity';
import './ProductDetailPage.css';

const fmt = (amount) => amount != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount)
    : 'N/A';

const parseColors = (s) => s ? s.split(',').map(c => {
    const [name, hex] = c.trim().split(':');
    return { name: name.trim(), hex: (hex || name).trim() };
}) : [];

const parseSizes = (s) => s ? s.split(',').map(x => x.trim()) : [];

/**
 * @param previewProduct  optional ProductResponseDTO-shaped object to render instead of fetching
 *                        /products/:productId — used by catalog import to show a draft exactly as
 *                        the shop will. Preview mode is look-only: cart, share, analytics, related
 *                        products and breadcrumb navigation are all switched off.
 */
const ProductDetailPage = ({ previewProduct } = {}) => {
    const { productId } = useParams();
    const isPreview = !!previewProduct;
    const navigate = useNavigate();

    const [product, setProduct]         = useState(null);
    const [isLoading, setIsLoading]     = useState(true);
    const [error, setError]             = useState(null);
    const [mainImage, setMainImage]     = useState('');
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize]   = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [quantity, setQuantity]       = useState(1);
    const [showZoom, setShowZoom]       = useState(false);
    const [related, setRelated]         = useState([]);
    const [activeTab, setActiveTab]     = useState('details');

    // Rejection inline panel state
    const [showRejectPanel, setShowRejectPanel] = useState(false);
    const [rejectFeedback, setRejectFeedback]   = useState('');
    const [isSubmittingReject, setIsSubmittingReject] = useState(false);

    // Cart interceptor state
    const [isCheckingCarts, setIsCheckingCarts] = useState(false);
    const [activeCarts, setActiveCarts]         = useState([]);
    const [showModal, setShowModal]             = useState(false);

    const imgRef    = useRef(null);
    const lensRef   = useRef(null);
    const resultRef = useRef(null);

    const { user }     = useAuth();
    const { addToCart } = useLocalCart();
    const { addToast } = useToast();

    const employeeRoles = ['EMPLOYEE', 'ADMIN', 'CURATOR'];

    useEffect(() => {
        // Default selections for a freshly loaded product — shared by the live page and preview.
        const initSelections = (p) => {
            const hasCombination = p.variants?.length > 0 && p.variants.some(v => v.color != null);
            if (hasCombination) {
                // Default to the first available color (and its first available
                // size, if this product has a size axis) so the page opens on a
                // real, purchasable selection instead of an empty "pick one" state.
                const firstColorVariant = p.variants.find(v => v.color != null && v.available !== false);
                const hasSizes = p.variants.some(v => v.size != null);
                if (firstColorVariant) {
                    setSelectedColor(firstColorVariant.color);
                    if (hasSizes) {
                        const firstSized = p.variants.find(v =>
                            v.color === firstColorVariant.color && v.size != null && v.available !== false);
                        if (firstSized) {
                            setSelectedSize(firstSized.size);
                            setSelectedVariant(firstSized);
                        }
                    } else {
                        setSelectedVariant(firstColorVariant);
                    }
                }
                const rep = p.variants.find(v => v.color === firstColorVariant?.color && v.imageUrl);
                setMainImage(rep ? rep.imageUrl : (p.imageUrls?.[0] || ''));
            } else {
                if (p.imageUrls?.length > 0) setMainImage(p.imageUrls[0]);
                if (p.variants?.length > 0) {
                    setSelectedVariant(p.variants[0]); // label variants — first option
                } else if (p.sizes) {
                    const sizeList = parseSizes(p.sizes);
                    if (sizeList.length > 0) setSelectedSize(sizeList[0]);
                }
            }
        };

        if (previewProduct) {
            setProduct(previewProduct);
            initSelections(previewProduct);
            setIsLoading(false);
            return;
        }

        const fetchProduct = async () => {
            setIsLoading(true);
            try {
                const response = await api.get(`/products/${productId}`);
                const p = response.data;
                setProduct(p);
                clarityEvent('product_viewed');
                initSelections(p);

                if (p.category) {
                    api.get(`/products?size=4&category=${encodeURIComponent(p.category)}`)
                        .then(r => setRelated((r.data?.content || []).filter(x => x.id !== p.id).slice(0, 4)))
                        .catch(() => {});
                }
            } catch {
                setError('Failed to fetch product details.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchProduct();
    }, [productId, previewProduct]);

    // ── Admin Workflow Handlers ──────────────────────────────────
    const handleApprove = async () => {
        try {
            await api.put(`/products/${product.id}/approve`);
            addToast('Product approved successfully!', 'success');
            setProduct(prev => ({ ...prev, approvalStatus: 'APPROVED', adminFeedback: null }));
        } catch {
            addToast('Failed to approve product.', 'error');
        }
    };

    const handleReject = () => setShowRejectPanel(true);

    const submitReject = async () => {
        if (!rejectFeedback.trim()) return;
        setIsSubmittingReject(true);
        try {
            await api.put(`/products/${product.id}/reject`, { feedback: rejectFeedback });
            addToast('Product rejected and sent back to employee.', 'success');
            setProduct(prev => ({ ...prev, approvalStatus: 'REJECTED', adminFeedback: rejectFeedback }));
            setShowRejectPanel(false);
            setRejectFeedback('');
        } catch {
            addToast('Failed to reject product.', 'error');
        } finally {
            setIsSubmittingReject(false);
        }
    };

    // ── Cart Handlers ────────────────────────────────────────────
    const handlePersonalCartAdd = () => {
        const variant = (product?.variants?.length > 0) ? selectedVariant : null;
        for (let i = 0; i < quantity; i++) {
            addToCart(product, variant);
            clarityEvent('add_to_cart');
        }
        const variantDesc = variant
            ? (variant.label || [variant.color, variant.size].filter(Boolean).join(' / '))
            : '';
        const variantSuffix = variantDesc ? ` (${variantDesc})` : '';
        addToast(`${quantity > 1 ? quantity + '×' : ''} ${product.name}${variantSuffix} added to cart`, 'success');
        setShowModal(false);
    };

    const handleAddToCartClick = async () => {
        if (!user) {
            handlePersonalCartAdd();
            return;
        }
        setIsCheckingCarts(true);
        try {
            const res = await api.get('/quotes/my-carts');
            const openCarts = (res.data || []).filter(c => c.status !== 'QUOTE');
            if (openCarts.length > 0) {
                setActiveCarts(openCarts);
                setShowModal(true);
            } else {
                handlePersonalCartAdd();
            }
        } catch {
            handlePersonalCartAdd();
        } finally {
            setIsCheckingCarts(false);
        }
    };

    const addToSharedWorkspace = async (cartId) => {
        const variant = product?.variants?.length > 0 ? selectedVariant : null;
        try {
            const wsVariantDesc = variant
                ? (variant.label || [variant.color, variant.size].filter(Boolean).join(' / '))
                : '';
            await api.post(`/quotes/${cartId}/add-item`, {
                productId: product.id,
                quantity,
                variantLabel: wsVariantDesc || null,
            });
            const variantSuffix = wsVariantDesc ? ` (${wsVariantDesc})` : '';
            addToast(`${product.name}${variantSuffix} added to workspace`, 'success');
            setShowModal(false);
        } catch (err) {
            addToast(err.response?.data?.error || 'Failed to add to workspace', 'error');
        }
    };

    // ── Image Zoom ───────────────────────────────────────────────
    const hasTrackedZoom = useRef(false);
    const handleMouseEnterImage = () => {
        setShowZoom(true);
        if (!isPreview && !hasTrackedZoom.current) { clarityEvent('product_image_zoomed'); hasTrackedZoom.current = true; }
    };

    const handleMouseMove = (e) => {
        if (!imgRef.current || !lensRef.current || !resultRef.current) return;
        e.preventDefault();
        const img    = imgRef.current;
        const lens   = lensRef.current;
        const result = resultRef.current;
        const cx = result.offsetWidth  / lens.offsetWidth;
        const cy = result.offsetHeight / lens.offsetHeight;
        const { left, top, width, height } = img.getBoundingClientRect();
        let x = e.pageX - left - window.scrollX;
        let y = e.pageY - top  - window.scrollY;
        x = Math.max(0, Math.min(x, width  - lens.offsetWidth));
        y = Math.max(0, Math.min(y, height - lens.offsetHeight));
        lens.style.left = x + 'px';
        lens.style.top  = y + 'px';
        result.style.backgroundImage    = `url('${mainImage}')`;
        result.style.backgroundSize     = `${width * cx}px ${height * cy}px`;
        result.style.backgroundPosition = `-${x * cx}px -${y * cy}px`;
    };

    const handleShare = () => {
        if (navigator.share) navigator.share({ title: product?.name, url: window.location.href });
        else { navigator.clipboard.writeText(window.location.href); addToast('Link copied to clipboard!', 'success'); }
    };

    if (isLoading) return <div className="loading-state">Loading product…</div>;
    if (error)     return <div className="error-state">{error}</div>;
    if (!product)  return <div className="no-products-found">Product not found.</div>;

    const colors = parseColors(product.colors);
    const sizes  = parseSizes(product.sizes);
    const hasVariants = product.variants && product.variants.length > 0;
    const hasCombinationVariants = hasVariants && product.variants.some(v => v.color != null);
    const variantColors = hasCombinationVariants
        ? [...new Map(product.variants.filter(v => v.color).map(v => [v.color, { name: v.color, hex: v.colorHex || v.color }])).values()]
        : [];
    const sizesForColor = hasCombinationVariants && selectedColor
        ? product.variants.filter(v => v.color === selectedColor && v.available !== false).map(v => v.size).filter(Boolean)
        : [];
    // Color-only products (no size axis at all, e.g. a mug that just comes in
    // different colors) skip the size step entirely — there's nothing to pick.
    const hasSizeAxis = hasCombinationVariants && product.variants.some(v => v.size != null);

    const offerPrice  = !hasVariants ? product.marketOperatingPriceWithGst : null;
    const hasDiscount = !hasVariants && offerPrice && offerPrice < product.mrp;
    const discountPct = hasDiscount ? Math.round(((product.mrp - offerPrice) / product.mrp) * 100) : 0;
    const price       = hasVariants ? (selectedVariant?.mrp ?? product.mrp) : (offerPrice || product.mrp);

    return (
        <div className="pdp-wrapper">
            {/* Breadcrumb */}
            <nav className="pdp-breadcrumb">
                {isPreview ? <span>Products</span> : <Link to="/products">Products</Link>}
                {product.category && (
                    <><span>›</span>{isPreview
                        ? <span>{product.category}</span>
                        : <Link to={`/products?category=${product.category}`}>{product.category}</Link>}</>
                )}
                {product.subcategory && (
                    <><span>›</span>{isPreview
                        ? <span>{product.subcategory}</span>
                        : <Link to={`/products?subcategory=${product.subcategory}`}>{product.subcategory}</Link>}</>
                )}
                <span>›</span><span>{product.name}</span>
            </nav>

            <div className="product-detail-container">
                {/* ── Images ── */}
                <div className="product-images-section">
                    <div
                        className="main-image-wrapper"
                        onMouseEnter={handleMouseEnterImage}
                        onMouseLeave={() => setShowZoom(false)}
                        onMouseMove={handleMouseMove}
                    >
                        <img ref={imgRef} src={mainImage} alt={product.name} className="product-detail-image" />
                        {showZoom && <div ref={lensRef} className="img-zoom-lens" />}
                        {hasDiscount && <div className="pdp-discount-ribbon">−{discountPct}%</div>}
                    </div>
                    <div ref={resultRef} className={`img-zoom-result ${showZoom ? 'active' : ''}`} />
                    <div className="thumbnail-gallery">
                        {product.imageUrls?.map((url, i) => (
                            <img
                                key={i}
                                src={url}
                                alt={`View ${i + 1}`}
                                className={`thumbnail ${url === mainImage ? 'active' : ''}`}
                                onClick={() => setMainImage(url)}
                            />
                        ))}
                    </div>
                </div>

                {/* ── Info ── */}
                <div className="product-detail-info">
                    {/* Status banner for employees/admins */}
                    {(product.approvalStatus === 'PENDING' || product.approvalStatus === 'REJECTED')
                     && user && employeeRoles.includes(user.role) && (
                        <div className={`pdp-status-banner ${product.approvalStatus.toLowerCase()}`}>
                            {product.approvalStatus === 'PENDING' && (
                                <span>
                                    <strong>⏳ PENDING APPROVAL</strong>
                                    {product.createdBy ? ` — Submitted by ${product.createdBy}` : ''}
                                </span>
                            )}
                            {product.approvalStatus === 'REJECTED' && (
                                <>
                                    <span><strong>⚠️ ACTION REQUIRED (REJECTED)</strong></span>
                                    {product.adminFeedback && (
                                        <span className="pdp-feedback">Reason: {product.adminFeedback}</span>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="pdp-top-row">
                        <span className="pdp-category-badge">{product.category}</span>
                        <button className="pdp-share-btn" onClick={handleShare} title="Share product" disabled={isPreview}>
                            ⤴ Share
                        </button>
                    </div>

                    <h1>{product.name}</h1>

                    {/* Price */}
                    <div className="price-section">
                        {hasDiscount ? (
                            <div className="detail-price-wrapper">
                                <span className="detail-price-current">{fmt(price)}</span>
                                <div className="detail-price-sub">
                                    <span className="detail-price-original">{fmt(product.mrp)}</span>
                                    <span className="detail-discount-badge">{discountPct}% OFF</span>
                                </div>
                            </div>
                        ) : (
                            <p className="price">{fmt(price)}</p>
                        )}
                        <p className="price-gst-label">incl. GST</p>
                    </div>

                    <p className="description">{product.description}</p>

                    {/* Colors */}
                    {colors.length > 0 && !hasCombinationVariants && (
                        <div className="attribute-section">
                            <h4>Color {selectedColor && <span className="pdp-selected-label">— {selectedColor}</span>}</h4>
                            <div className="color-swatches">
                                {colors.map((c, i) => (
                                    <div
                                        key={i}
                                        className={`color-swatch ${selectedColor === c.name ? 'selected' : ''}`}
                                        style={{ backgroundColor: c.hex }}
                                        onClick={() => setSelectedColor(c.name)}
                                        title={c.name}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Label variants (flat list with per-variant pricing) */}
                    {hasVariants && !hasCombinationVariants && (
                        <div className="attribute-section">
                            <h4>Size / Variant {selectedVariant && <span className="pdp-selected-label">— {selectedVariant.label}</span>}</h4>
                            <div className="size-boxes">
                                {product.variants.map((v, i) => (
                                    <div
                                        key={i}
                                        className={`size-box ${selectedVariant?.id === v.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedVariant(v)}
                                        title={fmt(v.mrp)}
                                    >
                                        <span>{v.label}</span>
                                        <span style={{ display: 'block', fontSize: '11px', opacity: 0.75 }}>{fmt(v.mrp)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Combination variants (Color × Size 2D picker) */}
                    {hasCombinationVariants && (
                        <>
                            <div className="attribute-section">
                                <h4>Color {selectedColor && <span className="pdp-selected-label">— {selectedColor}</span>}</h4>
                                <div className="color-swatches">
                                    {variantColors.map((c, i) => (
                                        <div
                                            key={i}
                                            className={`color-swatch ${selectedColor === c.name ? 'selected' : ''}`}
                                            style={{ backgroundColor: c.hex }}
                                            onClick={() => {
                                            setSelectedColor(c.name);
                                            setSelectedSize(null);
                                            // No size axis on this product — the color IS the whole
                                            // variant, so select it directly instead of waiting on a
                                            // size step that doesn't exist.
                                            const colorVariant = !hasSizeAxis
                                                ? product.variants.find(v => v.color === c.name && v.available !== false)
                                                : null;
                                            setSelectedVariant(colorVariant || null);
                                            const rep = product.variants.find(v => v.color === c.name && v.imageUrl);
                                            setMainImage(rep ? rep.imageUrl : (product.imageUrls?.[0] || mainImage));
                                        }}
                                            title={c.name}
                                        />
                                    ))}
                                </div>
                            </div>
                            {hasSizeAxis && (selectedColor ? (
                                <div className="attribute-section">
                                    <h4>Size {selectedSize && <span className="pdp-selected-label">— {selectedSize}</span>}</h4>
                                    {sizesForColor.length > 0 ? (
                                        <div className="size-boxes">
                                            {sizesForColor.map((s, i) => {
                                                const v = product.variants.find(vr => vr.color === selectedColor && vr.size === s);
                                                return (
                                                    <div
                                                        key={i}
                                                        className={`size-box ${selectedSize === s ? 'selected' : ''}`}
                                                        onClick={() => {
                                                            setSelectedSize(s);
                                                            setSelectedVariant(v || null);
                                                            if (v?.imageUrl) setMainImage(v.imageUrl);
                                                        }}
                                                        title={v ? fmt(v.mrp) : ''}
                                                    >
                                                        <span>{s}</span>
                                                        {v && <span style={{ display: 'block', fontSize: '11px', opacity: 0.75 }}>{fmt(v.mrp)}</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p style={{ fontSize: '13px', color: '#999' }}>No sizes available for this color</p>
                                    )}
                                </div>
                            ) : (
                                <p style={{ fontSize: '13px', color: '#888', marginTop: '-8px', marginBottom: '16px' }}>Select a color to see available sizes</p>
                            ))}
                        </>
                    )}

                    {/* Simple sizes (no per-variant pricing) */}
                    {!hasVariants && sizes.length > 0 && (
                        <div className="attribute-section">
                            <h4>Size {selectedSize && <span className="pdp-selected-label">— {selectedSize}</span>}</h4>
                            <div className="size-boxes">
                                {sizes.map((s, i) => (
                                    <div
                                        key={i}
                                        className={`size-box ${selectedSize === s ? 'selected' : ''}`}
                                        onClick={() => setSelectedSize(s)}
                                    >
                                        {s}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quantity */}
                    <div className="attribute-section">
                        <h4>Quantity</h4>
                        <div className="pdp-qty-row">
                            <button
                                className="pdp-qty-btn"
                                onClick={() => setQuantity(q => Math.max(1, (parseInt(q, 10) || 0) - 1))}
                            >−</button>
                            <input
                                type="number"
                                min="1"
                                className="pdp-qty-input"
                                value={quantity === '' ? '' : (parseInt(quantity, 10) || 0)}
                                onChange={(e) => {
                                    if (e.target.value === '') {
                                        setQuantity('');
                                    } else {
                                        const val = parseInt(e.target.value, 10);
                                        if (!isNaN(val)) setQuantity(val);
                                    }
                                }}
                                onBlur={() => {
                                    if (quantity === '' || quantity < 1) setQuantity(1);
                                }}
                            />
                            <button
                                className="pdp-qty-btn"
                                onClick={() => setQuantity(q => (parseInt(q, 10) || 0) + 1)}
                            >+</button>
                            <span className="pdp-qty-avail">{product.itemsAvailable} available</span>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="pdp-cta-row">
                        {user?.role === 'ADMIN' && product.approvalStatus === 'PENDING' ? (
                            <>
                                <div className="pdp-admin-actions">
                                    <button className="pdp-btn-approve" onClick={handleApprove}>Approve</button>
                                    <button className="pdp-btn-reject"  onClick={handleReject}>Reject</button>
                                </div>
                                {showRejectPanel && (
                                    <div className="pdp-reject-panel">
                                        <label>Reason for rejection</label>
                                        <textarea
                                            className="pdp-reject-textarea"
                                            placeholder="Explain what needs to be fixed…"
                                            value={rejectFeedback}
                                            onChange={e => setRejectFeedback(e.target.value)}
                                        />
                                        <div className="pdp-reject-actions">
                                            <button
                                                className="pdp-reject-submit"
                                                onClick={submitReject}
                                                disabled={!rejectFeedback.trim() || isSubmittingReject}
                                            >
                                                {isSubmittingReject ? 'Submitting…' : 'Confirm Rejection'}
                                            </button>
                                            <button
                                                className="pdp-reject-cancel"
                                                onClick={() => { setShowRejectPanel(false); setRejectFeedback(''); }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (product.approvalStatus === 'PENDING' || product.approvalStatus === 'REJECTED') ? (
                            <button className="add-to-cart-detail-btn" disabled>
                                Not Available for Purchase
                            </button>
                        ) : (
                            <button
                                className="add-to-cart-detail-btn"
                                onClick={handleAddToCartClick}
                                disabled={isPreview || isCheckingCarts || (hasCombinationVariants && !selectedVariant)}
                                title={isPreview ? 'Preview — not purchasable yet' : undefined}
                            >
                                {isCheckingCarts
                                    ? 'Checking workspaces…'
                                    : hasCombinationVariants && !selectedVariant
                                    ? 'Select Color & Size'
                                    : `Add to Cart — ${fmt(price * (parseInt(quantity, 10) || 1))}`}
                            </button>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="pdp-tabs">
                        <div className="pdp-tab-list">
                            {['details', 'specs', user && employeeRoles.includes(user.role) ? 'internal' : null]
                                .filter(Boolean)
                                .map(tab => (
                                    <button
                                        key={tab}
                                        className={`pdp-tab-btn ${activeTab === tab ? 'pdp-tab-btn--active' : ''}`}
                                        onClick={() => { setActiveTab(tab); if (!isPreview) clarityEvent('tab_switched'); }}
                                    >
                                        {tab === 'details' ? 'Product Details' : tab === 'specs' ? 'Specifications' : 'Internal Info'}
                                    </button>
                                ))}
                        </div>

                        {activeTab === 'details' && (
                            <div className="details-section pdp-tab-content">
                                <p><strong>Brand</strong>    {product.brand || 'N/A'}</p>
                                <p><strong>Category</strong> {product.category}</p>
                                {product.subcategory && <p><strong>Subcategory</strong> {product.subcategory}</p>}
                                {selectedVariant?.sku && <p><strong>SKU</strong> {selectedVariant.sku}</p>}
                                <p><strong>In Stock</strong> {product.itemsAvailable} units</p>
                            </div>
                        )}

                        {activeTab === 'specs' && (
                            <div className="details-section pdp-tab-content">
                                {product.dimensionL
                                    ? <p><strong>Dimensions</strong> {product.dimensionL} × {product.dimensionW} × {product.dimensionH} {product.dimensionUnit}</p>
                                    : <p>No specifications available.</p>
                                }
                            </div>
                        )}

                        {activeTab === 'internal' && user && employeeRoles.includes(user.role) && (
                            <div className="details-section employee-details pdp-tab-content">
                                <p><strong>Purchase Price</strong> {fmt(product.eyantraPurchasePriceWithGst)}</p>
                                <p><strong>Seller</strong>         {product.sellerName || 'N/A'}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Cart / workspace selection modal — portal to document.body so
                position:fixed is viewport-relative, not scoped to any ancestor transform */}
            {showModal && ReactDOM.createPortal(
                <div className="pc-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="pc-modal" onClick={e => e.stopPropagation()}>
                        <button className="pc-modal__close" onClick={() => setShowModal(false)}>✕</button>
                        <h3 className="pc-modal__title">Where should we add this?</h3>
                        <div className="pc-modal__options">
                            <button className="pc-modal__btn-personal" onClick={handlePersonalCartAdd}>
                                🛒 My Personal Cart
                            </button>
                            <hr className="pc-modal__divider" />
                            <span className="pc-modal__workspace-label">Collaborative Workspaces</span>
                            {activeCarts.map(cart => (
                                <button
                                    key={cart.id}
                                    className="pc-modal__btn-workspace"
                                    onClick={() => addToSharedWorkspace(cart.id)}
                                >
                                    📁 {cart.cartName}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Related products */}
            {related.length > 0 && (
                <section className="pdp-related">
                    <h2 className="pdp-related__title">
                        More from <span>{product.category}</span>
                    </h2>
                    <div className="pdp-related__grid">
                        {related.map(p => (
                            <div
                                key={p.id}
                                className="pdp-related__card"
                                onClick={() => { clarityEvent('related_product_clicked'); navigate(`/product/${p.id}`); }}
                            >
                                <div className="pdp-related__img">
                                    {(p.thumbnailUrl || p.imageUrl)
                                        ? <img src={p.thumbnailUrl || p.imageUrl} alt={p.name} />
                                        : <span>📦</span>
                                    }
                                </div>
                                <div className="pdp-related__info">
                                    <div className="pdp-related__name">{p.name}</div>
                                    <div className="pdp-related__price">{fmt(p.marketOperatingPriceWithGst || p.mrp)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default ProductDetailPage;